import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";

interface DailyCode {
  id: string;
  code: string;
  valid_date: string;
  created_at: string;
}

interface UseDailyAccessCodeReturn {
  todayCode: string | null;
  isLoading: boolean;
  generateNewCode: () => Promise<string | null>;
  refetch: () => Promise<void>;
}

function generateRandomCode(): string {
  // Gerar código de 6 dígitos alfanumérico (sem caracteres ambíguos)
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let code = "";
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

export function useDailyAccessCode(): UseDailyAccessCodeReturn {
  const { user } = useAuth();
  const { activeRestaurant } = useRestaurantContext();
  const [todayCode, setTodayCode] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchTodayCode = useCallback(async () => {
    if (!user || !activeRestaurant) {
      setTodayCode(null);
      setIsLoading(false);
      return;
    }

    try {
      const today = new Date().toISOString().split("T")[0];

      const { data, error } = await supabase
        .from("daily_access_codes")
        .select("*")
        .eq("restaurant_id", activeRestaurant.id)
        .eq("valid_date", today)
        .maybeSingle();

      if (error) throw error;

      setTodayCode(data?.code || null);
    } catch (error) {
      console.error("Erro ao buscar código do dia:", error);
      setTodayCode(null);
    } finally {
      setIsLoading(false);
    }
  }, [user, activeRestaurant]);

  useEffect(() => {
    fetchTodayCode();
  }, [fetchTodayCode]);

  const generateNewCode = async (): Promise<string | null> => {
    if (!user || !activeRestaurant) {
      toast.error("Usuário ou restaurante não encontrado");
      return null;
    }

    try {
      const today = new Date().toISOString().split("T")[0];
      const newCode = generateRandomCode();

      // Tentar inserir ou atualizar (upsert)
      const { error } = await supabase
        .from("daily_access_codes")
        .upsert(
          {
            restaurant_id: activeRestaurant.id,
            code: newCode,
            valid_date: today,
            created_by: user.id,
          },
          {
            onConflict: "restaurant_id,valid_date",
          }
        );

      if (error) throw error;

      setTodayCode(newCode);
      toast.success("Novo código gerado!");
      return newCode;
    } catch (error) {
      console.error("Erro ao gerar código:", error);
      toast.error("Erro ao gerar novo código");
      return null;
    }
  };

  return {
    todayCode,
    isLoading,
    generateNewCode,
    refetch: fetchTodayCode,
  };
}
